// Alert Configuration Modal Component
// Feature: 015-we-want-to
// T059: Modal for creating/editing collection alert configurations

import React, { useState, useEffect } from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { Badge } from '../../ui/components/Badge';
import { ToggleGroup } from '../../ui/components/ToggleGroup';
import { useCreateAlertConfig, useUpdateAlertConfig, useDeleteAlertConfig } from '../../hooks/useAlertConfigs';
import {
  type NotificationType,
  type AlertConfig,
  type CreateAlertConfigInput,
  type UpdateAlertConfigInput,
  type TraitFilters,
  type AlertDeliveryChannel,
  getNotificationTypeLabel,
  testDiscordWebhook,
} from '../../api/alertConfigs';
import { getRarityInfoInCategory } from '../../utils/formatters';
import { FeatherBell, FeatherX, FeatherAlertCircle, FeatherCheck, FeatherLoader, FeatherInbox, FeatherMessageCircle, FeatherChevronDown, FeatherChevronRight, FeatherTrash2 } from '@subframe/core';

// Trait stats with percentage data for rarity coloring
interface TraitValueStats {
  count: number;
  percentage: number;
}

interface TraitStatsMap {
  [traitType: string]: {
    [value: string]: TraitValueStats;
  };
}

interface AlertConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  collectionId: string;
  collectionName: string;
  notificationType: NotificationType;
  availableTraits?: {
    [traitType: string]: string[];
  };
  traitStats?: TraitStatsMap;
  /** Existing config for edit mode. If provided, modal opens in edit mode. */
  existingConfig?: AlertConfig;
}

export const AlertConfigModal: React.FC<AlertConfigModalProps> = ({
  isOpen,
  onClose,
  collectionId,
  collectionName,
  notificationType,
  availableTraits = {},
  traitStats,
  existingConfig,
}) => {
  const isEditMode = !!existingConfig;

  // Delivery channel: 'inbox' for in-app + email, 'discord' for webhook only
  const [deliveryChannel, setDeliveryChannel] = useState<AlertDeliveryChannel>('inbox');

  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');
  const [selectedTraits, setSelectedTraits] = useState<TraitFilters>({});
  const [expandedTraitTypes, setExpandedTraitTypes] = useState<Set<string>>(new Set());
  const [isTraitFiltersExpanded, setIsTraitFiltersExpanded] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Discord webhook state (only used when deliveryChannel is 'discord')
  const [discordWebhookUrl, setDiscordWebhookUrl] = useState<string>('');
  const [isTestingWebhook, setIsTestingWebhook] = useState<boolean>(false);
  const [webhookTestResult, setWebhookTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  const { mutate: createConfig, isPending: isCreating, isSuccess: isCreateSuccess, isError: isCreateError, error: createError } = useCreateAlertConfig();
  const { mutate: updateConfig, isPending: isUpdating, isSuccess: isUpdateSuccess, isError: isUpdateError, error: updateError } = useUpdateAlertConfig();
  const { mutate: deleteConfig, isPending: isDeleting } = useDeleteAlertConfig();

  const isPending = isCreating || isUpdating || isDeleting;
  const isSuccess = isCreateSuccess || isUpdateSuccess;

  // Populate form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (existingConfig) {
        // Edit mode: populate with existing values
        setDeliveryChannel(existingConfig.delivery_channel || 'inbox');
        setMinPrice(existingConfig.min_amount_in_xrp !== null ? String(existingConfig.min_amount_in_xrp) : '');
        setMaxPrice(existingConfig.max_amount_in_xrp !== null ? String(existingConfig.max_amount_in_xrp) : '');
        setSelectedTraits(existingConfig.trait_filters || {});
        setDiscordWebhookUrl(existingConfig.discord_webhook_url || '');
        // Expand trait filters if there are selected traits
        const hasTraitFilters = existingConfig.trait_filters && Object.keys(existingConfig.trait_filters).length > 0;
        setIsTraitFiltersExpanded(hasTraitFilters || false);
      } else {
        // Create mode: reset to defaults
        setDeliveryChannel('inbox');
        setMinPrice('');
        setMaxPrice('');
        setSelectedTraits({});
        setDiscordWebhookUrl('');
        setIsTraitFiltersExpanded(false);
      }
      setExpandedTraitTypes(new Set());
      setError('');
      setWebhookTestResult(null);
      setShowDeleteConfirm(false);
    }
  }, [isOpen, existingConfig]);

  // Close modal on success (with small delay to ensure cache refetch completes)
  useEffect(() => {
    if (isSuccess) {
      // Wait for cache invalidation to trigger refetch
      const timeout = setTimeout(() => {
        onClose();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isSuccess, onClose]);

  // Display mutation error
  useEffect(() => {
    const mutationError = createError || updateError;
    if ((isCreateError || isUpdateError) && mutationError) {
      setError(mutationError.message || `Failed to ${isEditMode ? 'update' : 'create'} alert configuration`);
    }
  }, [isCreateError, isUpdateError, createError, updateError, isEditMode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate Discord webhook URL is provided when delivery channel is 'discord'
    if (deliveryChannel === 'discord' && !discordWebhookUrl) {
      setError('Discord webhook URL is required');
      return;
    }

    // Validate min/max relationship
    const minPriceNum = minPrice ? parseFloat(minPrice) : null;
    const maxPriceNum = maxPrice ? parseFloat(maxPrice) : null;

    if (minPriceNum !== null && maxPriceNum !== null && minPriceNum > maxPriceNum) {
      setError('Minimum price must be less than or equal to maximum price');
      return;
    }

    // Build trait filters (only include traits with selections)
    const traitFilters: TraitFilters = {};
    Object.entries(selectedTraits).forEach(([traitType, values]) => {
      if (values.length > 0) {
        traitFilters[traitType] = values;
      }
    });

    if (isEditMode && existingConfig) {
      // Update existing config
      const input: UpdateAlertConfigInput = {
        min_amount_in_xrp: minPriceNum,
        max_amount_in_xrp: maxPriceNum,
        trait_filters: Object.keys(traitFilters).length > 0 ? traitFilters : null,
        delivery_channel: deliveryChannel,
        discord_webhook_url: deliveryChannel === 'discord' ? discordWebhookUrl : null,
        discord_enabled: deliveryChannel === 'discord',
      };

      updateConfig({ id: existingConfig.id, input });
    } else {
      // Create new config
      const input: CreateAlertConfigInput = {
        collection_id: collectionId,
        notification_type: notificationType,
        min_amount_in_xrp: minPriceNum,
        max_amount_in_xrp: maxPriceNum,
        trait_filters: Object.keys(traitFilters).length > 0 ? traitFilters : null,
        delivery_channel: deliveryChannel,
        discord_webhook_url: deliveryChannel === 'discord' ? discordWebhookUrl : null,
        discord_enabled: deliveryChannel === 'discord',
      };

      createConfig(input);
    }
  };

  const handleDelete = () => {
    if (!existingConfig) return;

    deleteConfig(existingConfig.id, {
      onSuccess: () => {
        onClose();
      },
      onError: (err) => {
        setError(err.message || 'Failed to delete alert configuration');
        setShowDeleteConfirm(false);
      },
    });
  };

  const handleTestWebhook = async () => {
    if (!discordWebhookUrl) {
      setWebhookTestResult({ success: false, message: 'Please enter a webhook URL' });
      return;
    }

    setIsTestingWebhook(true);
    setWebhookTestResult(null);

    try {
      const result = await testDiscordWebhook(discordWebhookUrl);
      setWebhookTestResult({
        success: result.success,
        message: result.success ? 'Test message sent!' : result.message || 'Failed to send test message',
      });
    } catch (err) {
      setWebhookTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to test webhook',
      });
    } finally {
      setIsTestingWebhook(false);
    }
  };

  const handleTraitToggle = (traitType: string, value: string) => {
    setSelectedTraits(prev => {
      const currentValues = prev[traitType] || [];
      const isSelected = currentValues.includes(value);

      if (isSelected) {
        // Remove value
        const newValues = currentValues.filter(v => v !== value);
        if (newValues.length === 0) {
          const { [traitType]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [traitType]: newValues };
      } else {
        // Add value
        return { ...prev, [traitType]: [...currentValues, value] };
      }
    });
  };

  const isTraitSelected = (traitType: string, value: string): boolean => {
    return selectedTraits[traitType]?.includes(value) || false;
  };

  const selectedTraitCount = Object.values(selectedTraits).reduce(
    (total, values) => total + values.length,
    0
  );

  // Get appropriate icon and description for notification type
  const getTypeConfig = (type: NotificationType) => {
    switch (type) {
      case 'COLLECTION_NEW_LISTING':
        return { icon: '🏷️', description: 'Get alerted when NFTs are listed for sale' };
      case 'COLLECTION_NEW_FLOOR':
        return { icon: '📉', description: 'Get alerted when the floor price drops' };
      case 'COLLECTION_SALE':
        return { icon: '💰', description: 'Get alerted when NFTs are sold' };
      case 'COLLECTION_MINT':
        return { icon: '🎨', description: 'Get alerted when new NFTs are minted' };
      default:
        return { icon: '🔔', description: 'Get alerted about collection activity' };
    }
  };

  const typeConfig = getTypeConfig(notificationType);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content className="w-full max-w-[600px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="flex w-full flex-col items-start gap-6 p-6">
          {/* Header */}
          <div className="flex w-full items-start justify-between">
            <div className="flex flex-col items-start gap-1">
              <div className="flex items-center gap-2">
                <span className="text-xl">{typeConfig.icon}</span>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {isEditMode ? 'Edit' : 'New'} {getNotificationTypeLabel(notificationType)} Alert
                </span>
              </div>
              <span className="text-body font-body text-subtext-color">
                {collectionName}
              </span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          </div>

          {/* Description */}
          <p className="text-caption font-caption text-subtext-color -mt-2">
            {typeConfig.description}
          </p>

          {/* Price Filters - Hidden for floor and mint alerts */}
          {notificationType !== 'COLLECTION_NEW_FLOOR' && notificationType !== 'COLLECTION_MINT' && (
            <div className="w-full flex flex-col gap-4">
              <span className="text-label font-label text-default-font">
                Price Filters (Optional)
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextField variant="filled" label="Min Price (XRP)" helpText="">
                  <TextField.Input
                    placeholder="e.g., 100"
                    value={minPrice}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMinPrice(e.target.value)}
                    type="number"
                    min="0"
                    step="0.000001"
                  />
                </TextField>
                <TextField variant="filled" label="Max Price (XRP)" helpText="">
                  <TextField.Input
                    placeholder="e.g., 1000"
                    value={maxPrice}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxPrice(e.target.value)}
                    type="number"
                    min="0"
                    step="0.000001"
                  />
                </TextField>
              </div>            <p className="text-caption font-caption text-subtext-color">
                Leave empty to receive alerts for all prices
              </p>
            </div>
          )}

          {/* Trait Filters - Collapsible (hidden for mint alerts - traits may not be loaded at mint time) */}
          {notificationType !== 'COLLECTION_MINT' && Object.keys(availableTraits).length > 0 && (
            <div className="w-full flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setIsTraitFiltersExpanded(!isTraitFiltersExpanded)}
                className="flex items-center justify-between w-full py-2 text-left hover:bg-neutral-50 rounded transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isTraitFiltersExpanded ? (
                    <FeatherChevronDown className="h-4 w-4 text-subtext-color" />
                  ) : (
                    <FeatherChevronRight className="h-4 w-4 text-subtext-color" />
                  )}
                  <span className="text-label font-label text-default-font">
                    Trait Filters (Optional)
                  </span>
                </div>
                {selectedTraitCount > 0 && (
                  <Badge variant="neutral">
                    {selectedTraitCount} selected
                  </Badge>
                )}
              </button>

              {isTraitFiltersExpanded && (
                <div className="flex flex-col gap-4 pl-6">
                  <div className="flex flex-col gap-4 max-h-[250px] overflow-y-auto">
                    {Object.entries(availableTraits).map(([traitType, values]) => {
                      // Sort values by rarity (rarest first) if stats available
                      const sortedValues = [...values].sort((a, b) => {
                        const percentA = traitStats?.[traitType]?.[a]?.percentage ?? 100;
                        const percentB = traitStats?.[traitType]?.[b]?.percentage ?? 100;
                        return percentA - percentB;
                      });

                      const isExpanded = expandedTraitTypes.has(traitType);
                      const displayLimit = 20;
                      const valuesToShow = isExpanded ? sortedValues : sortedValues.slice(0, displayLimit);
                      const hiddenCount = sortedValues.length - displayLimit;

                      return (
                        <div key={traitType} className="flex flex-col gap-2">
                          <span className="text-body-bold font-body-bold text-default-font">
                            {traitType}
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {valuesToShow.map((value) => {
                              const isSelected = isTraitSelected(traitType, value);
                              const percentage = traitStats?.[traitType]?.[value]?.percentage;
                              const allPercentages = traitStats?.[traitType] ? Object.values(traitStats[traitType]).map(v => v.percentage) : [];
                              const rarityInfo = getRarityInfoInCategory(percentage, allPercentages);
                              const isLegendary = !isSelected && rarityInfo.tier === 'legendary';

                              // Style based on selection state and rarity
                              const buttonStyle = isSelected
                                ? {} // Use class styling for selected
                                : {
                                    backgroundColor: `${rarityInfo.color}15`,
                                    color: rarityInfo.color,
                                    borderColor: rarityInfo.color,
                                  };

                              return (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() => handleTraitToggle(traitType, value)}
                                  className={`px-3 py-1 rounded-full text-caption font-caption transition-colors border ${
                                    isSelected
                                      ? 'bg-brand-600 text-white border-brand-600'
                                      : 'hover:opacity-80'
                                  }`}
                                  style={buttonStyle}
                                >
                                  <span className={isLegendary ? 'legendary-shimmer' : ''}>
                                    {value}
                                    {percentage !== undefined && (
                                      <span className={isLegendary ? '' : 'ml-1 opacity-70'}>
                                        {' '}{percentage < 1 ? percentage.toFixed(1) : Math.round(percentage)}%
                                      </span>
                                    )}
                                  </span>
                                </button>
                              );
                            })}
                            {!isExpanded && hiddenCount > 0 && (
                              <button
                                type="button"
                                onClick={() => setExpandedTraitTypes(prev => new Set([...prev, traitType]))}
                                className="px-3 py-1 text-caption font-caption text-brand-600 hover:text-brand-700 hover:underline cursor-pointer"
                              >
                                +{hiddenCount} more
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-caption font-caption text-subtext-color">
                    Select specific traits to filter alerts (AND between trait types, OR within same type)
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Delivery Channel Selector */}
          <div className="w-full flex flex-col gap-2">
            <span className="text-label font-label text-default-font">
              Send alerts to
            </span>
            <ToggleGroup
              value={deliveryChannel}
              onValueChange={(value) => value && setDeliveryChannel(value as AlertDeliveryChannel)}
            >
              <ToggleGroup.Item value="inbox" icon={<FeatherInbox />}>
                Inbox
              </ToggleGroup.Item>
              <ToggleGroup.Item value="discord" icon={<FeatherMessageCircle />}>
                Discord
              </ToggleGroup.Item>
            </ToggleGroup>
            <p className="text-caption font-caption text-subtext-color">
              {deliveryChannel === 'inbox'
                ? 'Alerts appear in your notifications inbox and may trigger emails'
                : 'Alerts are sent directly to your Discord channel via webhook'}
            </p>
          </div>

          {/* Discord Webhook URL - Only shown when Discord delivery is selected */}
          {deliveryChannel === 'discord' && (
            <div className="w-full flex flex-col gap-3">
              <TextField variant="filled" label="Discord Webhook URL" helpText="">
                <TextField.Input
                  placeholder="https://discord.com/api/webhooks/..."
                  value={discordWebhookUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setDiscordWebhookUrl(e.target.value);
                    setWebhookTestResult(null);
                  }}
                  type="url"
                />
              </TextField>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="neutral-secondary"
                  size="small"
                  onClick={handleTestWebhook}
                  disabled={isTestingWebhook || !discordWebhookUrl}
                >
                  {isTestingWebhook ? (
                    <>
                      <FeatherLoader className="h-4 w-4 animate-spin mr-2" />
                      Testing...
                    </>
                  ) : (
                    'Test Webhook'
                  )}
                </Button>
                {webhookTestResult && (
                  <div className={`flex items-center gap-1 text-caption font-caption ${webhookTestResult.success ? 'text-success-600' : 'text-error-600'}`}>
                    {webhookTestResult.success ? (
                      <FeatherCheck className="h-4 w-4" />
                    ) : (
                      <FeatherAlertCircle className="h-4 w-4" />
                    )}
                    {webhookTestResult.message}
                  </div>
                )}
              </div>

              <p className="text-caption font-caption text-subtext-color">
                Get your webhook URL from Discord: Server Settings → Integrations → Webhooks → New Webhook
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="w-full flex items-center gap-2 px-4 py-3 rounded bg-error-50 border border-error-200">
              <FeatherAlertCircle className="h-5 w-5 text-error-600 flex-shrink-0" />
              <span className="text-caption font-caption text-error-700">
                {error}
              </span>
            </div>
          )}

          {/* Delete Confirmation */}
          {isEditMode && showDeleteConfirm && (
            <div className="w-full flex flex-col gap-3 px-4 py-3 rounded bg-error-50 border border-error-200">
              <span className="text-body-bold font-body-bold text-error-700">
                Delete this alert?
              </span>
              <span className="text-caption font-caption text-error-600">
                You will no longer receive notifications for this alert configuration.
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="destructive-primary"
                  size="small"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  loading={isDeleting}
                >
                  Yes, Delete
                </Button>
                <Button
                  type="button"
                  variant="neutral-secondary"
                  size="small"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="w-full flex items-center justify-between gap-3">
            {/* Delete button (only in edit mode) */}
            {isEditMode && !showDeleteConfirm ? (
              <Button
                type="button"
                variant="destructive-secondary"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isPending}
                icon={<FeatherTrash2 />}
              >
                Delete
              </Button>
            ) : (
              <div /> // Spacer
            )}

            <div className="flex items-center gap-3">
              <Button
                type="button"
                onClick={onClose}
                variant="neutral-secondary"
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="brand-primary"
                disabled={isPending || showDeleteConfirm}
                loading={isCreating || isUpdating}
              >
                {isEditMode ? 'Save Changes' : 'Create Alert'}
              </Button>
            </div>
          </div>
        </form>
      </Dialog.Content>
    </Dialog>
  );
};
